import { Component, OnDestroy, OnInit, ViewEncapsulation, ApplicationRef, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { Subject, takeUntil, timer, tap, filter } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { BodyRendererService } from './common/services/body.renderer/body.renderer.service';
import { ShadowStyleLoaderService } from './common/services/shadow.style.loader/shadow.style.loader.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ImGoing';
  private onDestroy = new Subject<void>();

  private static readonly HOST_SCROLL_KEYS = new Set([
    ' ',
    'Spacebar',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
  ]);

  constructor(
    updates: SwUpdate,
    private router: Router,
    private bodyRendererService: BodyRendererService,
    private appRef: ApplicationRef,
    private cdRef: ChangeDetectorRef,
    private shadowStyleLoader: ShadowStyleLoaderService
  ) {
    console.log('constructor of AppComponent');

    if (!updates.isEnabled) {
      return;
    }

    updates.versionUpdates.pipe(takeUntil(this.onDestroy)).subscribe((event) => {
      updates.activateUpdate().then((isUpdated: boolean) => {
        isUpdated && location.reload();
      });
    });
  }

  async ngOnInit() {
    console.log('ngOnInit of AppComponent');
    this.hideSkeleton();

    // Subscribe to router events to trigger change detection on navigation
    // This ensures components get ng-star-inserted class when they're inserted
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.onDestroy),
        tap(() => {
          // Trigger change detection after navigation completes
          // This is a global fix for missing ng-star-inserted class
          timer(100)
            .pipe(
              tap(() => {
                this.cdRef.detectChanges();
                this.appRef.tick();
              })
            )
            .subscribe();
        })
      )
      .subscribe();

    // In embedded/script mode the app is bootstrapped with skipLocationChange, so the
    // browser URL bar stays at the host's hub-less path (e.g. /trip-planner). On browser
    // back/forward Angular re-resolves that hub-less URL and captures a feature path
    // (e.g. "trip-planner") as :hubName, breaking the page (blank route + 500 from the
    // travel-buddy calls). Detect those popstate navigations and re-pin the widget to its
    // configured route.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart =>
            event instanceof NavigationStart && event.navigationTrigger === 'popstate'
        ),
        takeUntil(this.onDestroy)
      )
      .subscribe((event) => {
        const realHub = this.getWrapperHub();
        if (!realHub) {
          return; // not embedded, or no route configured: leave back/forward as-is
        }
        const path = event.url.split('?')[0];
        const isInHub = path === `/${realHub}` || path.startsWith(`/${realHub}/`);
        if (!isInHub) {
          this.navigateToWrapperRoute();
        }
      });

    const wrapper =
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix');

    // for script solution, jump to the route defined in the wrapper element
    if (wrapper) {
      document.addEventListener('keydown', this.shieldTypingFromHostScroll);
      const root = document.getElementsByTagName('app-root')[0];
      if (root?.shadowRoot) {
        this.shadowStyleLoader.injectBaseStyles(root.shadowRoot);
      }
      this.navigateToWrapperRoute();
    } else {
      this.shadowStyleLoader.injectGlobalPreflight();

      // for non-script solution, if app.component is using shadow dom, we need to add the css file (the tailwind bundle) to the shadow dom
      // for script solution, the css file is loaded in the imgoingscript.js
      const root = document.getElementsByTagName('app-root')[0];
      if (root) {
        const shadowRoot = root.shadowRoot;
        // for running on localhost
        if (location.hostname === 'localhost') {
          if (environment.production === true) {
            const currentPath = location.pathname;
            const styleMatch = currentPath.match(/style(\S*).css/);
            if (styleMatch && styleMatch[0]) {
              localStorage.setItem('STYLES_URL', styleMatch[0]);
              this.shadowStyleLoader.injectBaseStyles(shadowRoot);
            } else {
              console.error('No matching styles.css file found in the current path.');
            }
          } else {
            localStorage.setItem('STYLES_URL', 'styles.css');
            this.shadowStyleLoader.injectBaseStyles(shadowRoot);
          }
        } else {
          // if visit the site next.imgoingcalendar.com, we need to extract the css file (as its name is hashed) and add to shadow dom
          // const domain = 'https://next.imgoingcalendar.com/';
          const domain = environment.frontendDomain;
          this.httpGetAsync(domain, (res) => {
            let style = res.match(/style(\S*).css/)[1];
            style = 'style' + style + '.css';

            // for place/event details page (shadow dom) to add the style
            console.log('--------------- styles -----------------', style);
            localStorage.setItem('STYLES_URL', domain + style);
            this.shadowStyleLoader.injectBaseStyles(shadowRoot);
          });
        }
        this.setHeight();
      }
    }
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.shieldTypingFromHostScroll);
    this.bodyRendererService.removeFromBody();
    this.onDestroy.next();
  }

  // From Youssef, CLS-502: Cherokee site's WordPress theme (Divi) loads smoothscroll.js, which listens for keys on window. It scrolls the page for Space, Page Up/Down, Home/End, and the arrow keys, and blocks the key's normal effect. It's meant to leave text boxes alone by checking event.target. But our widget renders inside a Shadow DOM, so a key press from our search box reaches Divi with app-root as its target instead of the <input>. Divi assumes the user isn't typing, so the page jumps and the space never appears in the box.
  // Fix: I added one keydown listener on document in web/src/app/app.component.ts, active only when the widget is embedded on a client site. It stops those navigation keys before they reach Divi, but only when they come from a text box, dropdown, or editable field inside our widget. It doesn't block the keys themselves, so typing and cursor movement work normally, and Divi's scrolling still works everywhere else on the client's page.
  private shieldTypingFromHostScroll = (event: KeyboardEvent) => {
    if (!AppComponent.HOST_SCROLL_KEYS.has(event.key)) {
      return;
    }

    const path = event.composedPath();
    const origin = path[0] as HTMLElement;
    if (!origin || origin === event.target) {
      return;
    }

    const isEditable =
      origin.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(origin.nodeName);
    if (!isEditable) {
      return;
    }

    const isInsideWidget = path.some(
      (node) =>
        node instanceof HTMLElement &&
        (node.tagName === 'APP-ROOT' || node.classList.contains('cdk-overlay-container'))
    );
    if (isInsideWidget) {
      event.stopPropagation();
    }
  };

  // Returns the real hub (first path segment of the wrapper's `route` attribute),
  // or null when not running as an embedded widget. Matches BaseComponent.getClientName().
  private getWrapperHub(): string | null {
    const wrapper =
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix');
    if (!wrapper) {
      return null;
    }
    const route = wrapper.attributes['route'] || wrapper.attributes['data-route'];
    if (!route) {
      return null;
    }
    const url: string = route.value.split('?')[0];
    const noSlash = url.startsWith('/') ? url.substr(1) : url;
    const slashIndex = noSlash.indexOf('/');
    return slashIndex > 0 ? noSlash.substr(0, slashIndex) : noSlash;
  }

  // Navigate to the route defined on the wrapper element, keeping the host URL unchanged
  // (skipLocationChange). Used both on initial bootstrap and to re-pin the widget when a
  // browser back/forward would otherwise resolve to a hub-less route.
  private navigateToWrapperRoute() {
    const wrapper =
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix');
    if (!wrapper) {
      return;
    }

    // adding "data-" to html element, as some CMS would remove other attributes
    const route = wrapper.attributes['route'] || wrapper.attributes['data-route'];
    console.log(route);
    if (!route) {
      alert(
        'Please specify the default URL for ImGoing in the wrapper element, e.g., <div id="imgoingcalendar-wrapper" route="/demo/events"></div>'
      );
      return;
    }

    // MacombIL has weird issue, the animation not working properly, here we manually add it
    if (route.value.includes('MacombIL')) {
      setTimeout(() => {
        this.intersect();
      }, 300);
    }

    const paramsObject = {};
    if (route.value.includes('?')) {
      // Create a URLSearchParams object with the query string parameters
      const searchParams = new URLSearchParams(route.value.split('?')[1]);

      for (const [key, value] of searchParams) {
        paramsObject[key] = value;
      }

      console.log('paramsObject', paramsObject);
    }

    this.router
      .navigate([route.value.split('?')[0]], {
        skipLocationChange: true,
        queryParams: paramsObject,
      })
      .then(() => {
        // Trigger global change detection after navigation completes
        // This ensures all components get ng-star-inserted class and render properly
        // This is especially important for script solution where Angular bootstraps dynamically
        timer(200)
          .pipe(
            tap(() => {
              // Force change detection to ensure all components are properly inserted
              this.cdRef.detectChanges();
              this.appRef.tick();
              this.hideExternalListingShell(wrapper as HTMLElement);
              console.log('Global change detection triggered after navigation');
            })
          )
          .subscribe();
      });
  }

  private hideSkeleton() {
    const wholeSpinner = document.getElementById('skeleton-mask');
    if (wholeSpinner) {
      wholeSpinner.style.display = 'none';
    }
  }

  private hideExternalListingShell(wrapper: HTMLElement | null) {
    if (!wrapper || typeof wrapper.closest !== 'function') {
      return;
    }
    if (wrapper.getAttribute('data-imgoing-ssr-listing') === 'trip-planner') {
      return;
    }

    const contentStage = wrapper.closest('.ig-ssr-content-stage') as HTMLElement | null;
    const shell = (contentStage?.querySelector(
      '#ig-ssr-listing-shell[data-ssr-list]'
    ) || null) as HTMLElement | null;

    if (!shell || shell.contains(wrapper) || shell.getAttribute('data-ig-widget-hidden') === 'true') {
      return;
    }

    shell.setAttribute('data-ig-widget-hidden', 'true');
    shell.classList.add('is-hidden');

    window.setTimeout(() => {
      if (shell.parentNode) {
        shell.parentNode.removeChild(shell);
      }
    }, 320);
  }

  private httpGetAsync(theUrl, callback) {
    let xmlHttp = new XMLHttpRequest();
    xmlHttp.onreadystatechange = function () {
      if (xmlHttp.readyState === 4 && xmlHttp.status === 200) {
        callback(xmlHttp.responseText);
      }
    };
    xmlHttp.open('GET', theUrl, true); // true for asynchronous
    xmlHttp.send(null);
  }

  private intersect() {
    // root is the browser viewport / screen
    var observer = new IntersectionObserver(
      function (entries) {
        // since there is a single target to be observed, there will be only one entry
        if (entries[0]['isIntersecting'] === true) {
          // if (entries[0]['intersectionRatio'] > 0.5)
          //   console.log('More than 50% of target is visible in the screen');
          entries[0].target.classList.add('fl-animated');
        }
      },
      { threshold: [0, 0.5, 1] }
    );

    const eles = document.querySelectorAll('.fl-animation');
    const right = document.querySelector('#bottom-right-card');
    // const eles = document.querySelectorAll('.div');
    console.log('eles.length ----', eles.length);
    eles.forEach((ele) => {
      observer.observe(ele as Element);
    });
    observer.observe(right as Element);
  }

  private setHeight() {
    document.body.style.height = '100%';
    document.querySelector('html').style.height = '100%';
  }
}
